import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

import ThemeLogo from "@/components/ThemeLogo";
import dryIceIcon from "@/assets/dutch-dryice-icon.png";
import { Menu, X, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalePath } from "@/hooks/useLocalePath";
import { useABTest } from "@/hooks/useABTest";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const PublicNavbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const { t } = useLanguage();
  const lp = useLocalePath();
  const { variantConfig: navConfig, trackEvent: trackNav } = useABTest("nav_bestellen");

  const solutions = [
    { href: lp("/oplossingen/horeca-catering"), label: t("landing.branchHoreca") },
    { href: lp("/oplossingen/medisch-farmaceutisch"), label: t("landing.branchPharma") },
    { href: lp("/oplossingen/transport-logistiek"), label: t("landing.branchTransport") },
    { href: lp("/oplossingen/voedselindustrie"), label: t("landing.branchFood") },
    { href: lp("/oplossingen/industrieel-reinigen"), label: t("landing.branchCleaning") },
    { href: lp("/oplossingen/evenementen"), label: t("landing.branchEvents") },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isSolutionActive = solutions.some((s) => location.pathname === s.href);

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border" aria-label="Hoofdnavigatie">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to={lp("/")} className="flex items-center">
            <img src={dryIceIcon} alt="Dutch DryIce" width={200} height={200} className="w-8 h-8 rounded-lg md:hidden" />
            <div className="hidden md:block">
              <ThemeLogo className="h-7" />
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1 text-sm font-medium">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={`bg-transparent hover:bg-secondary ${isSolutionActive ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    {t("nav.solutions")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[320px] p-3 space-y-1">
                      {solutions.map((s) => (
                        <Link
                          key={s.href}
                          to={s.href}
                          className={`block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-secondary ${
                            isActive(s.href) ? "bg-secondary font-medium text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to={lp("/kennisbank")}
              className={`px-4 py-2 rounded-md transition-colors ${
                location.pathname.startsWith(lp("/kennisbank")) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.kennisbank")}
            </Link>
            <Link
              to={lp("/faq")}
              className={`px-4 py-2 rounded-md transition-colors ${
                isActive(lp("/faq")) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.faq")}
            </Link>
            <Link
              to={lp("/over-ons")}
              className={`px-4 py-2 rounded-md transition-colors ${
                isActive(lp("/over-ons")) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.about")}
            </Link>
            <Link
              to={lp("/contact")}
              className={`px-4 py-2 rounded-md transition-colors ${
                isActive(lp("/contact")) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("nav.contact")}
            </Link>
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <Button variant="ghost" asChild>
              <Link to={lp("/login")}>{t("nav.login")}</Link>
            </Button>
            <Button asChild onClick={() => trackNav("click")}>
              <Link to={lp("/bestellen")}>{navConfig?.ctaText || t("nav.order")}</Link>
            </Button>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Menu openen">
                  <Menu className="w-5 h-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] p-0">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <Link to={lp("/")} onClick={() => setMobileOpen(false)} className="flex items-center">
                      <ThemeLogo className="h-7" variant="light" />
                    </Link>
                  </div>

                  <div className="p-4 space-y-2 border-b border-border">
                    <Button className="w-full" asChild onClick={() => { setMobileOpen(false); trackNav("click"); }}>
                      <Link to={lp("/bestellen")}>{navConfig?.ctaText || t("nav.order")}</Link>
                    </Button>
                    <Button variant="outline" className="w-full" asChild onClick={() => setMobileOpen(false)}>
                      <Link to={lp("/login")}>{t("nav.login")}</Link>
                    </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-1">
                    <button
                      onClick={() => setSolutionsOpen(!solutionsOpen)}
                      className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary ${
                        isSolutionActive ? "text-foreground bg-secondary" : "text-muted-foreground"
                      }`}
                    >
                      {t("nav.solutions")}
                      <ChevronDown className={`w-4 h-4 transition-transform ${solutionsOpen ? "rotate-180" : ""}`} />
                    </button>
                    {solutionsOpen && (
                      <div className="pl-3 space-y-0.5">
                        {solutions.map((s) => (
                          <Link
                            key={s.href}
                            to={s.href}
                            onClick={() => setMobileOpen(false)}
                            className={`block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-secondary ${
                              isActive(s.href) ? "bg-secondary font-medium text-foreground" : "text-muted-foreground"
                            }`}
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    )}

                    {[
                      { href: lp("/kennisbank"), label: t("nav.kennisbank") },
                      { href: lp("/faq"), label: t("nav.faq") },
                      { href: lp("/over-ons"), label: t("nav.about") },
                      { href: lp("/contact"), label: t("nav.contact") },
                    ].map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary ${
                          isActive(item.href) ? "text-foreground bg-secondary" : "text-muted-foreground"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("language.switch")}</span>
                    <LanguageSwitcher />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PublicNavbar;
